import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { CenteredLoader } from '@/components/CenteredLoader';
import { ErrorPanel } from '@/components/ErrorPanel';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <CenteredLoader label="Checking your session…" />;
  if (status === 'anonymous') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function _RoleGate({
  ok, label, children,
}: { ok: boolean; label: string; children: ReactNode }) {
  if (!ok) return (
    <ErrorPanel
      title="You don't have access to this area"
      detail={label}
      action={{ label: 'Back', onClick: () => window.history.back() }}
    />
  );
  return <>{children}</>;
}

export function RequirePatient({ children }: { children: ReactNode }) {
  return <RequireAuth>{children}</RequireAuth>;
}

export function RequireClinician({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <RequireAuth>
      <_RoleGate
        ok={Boolean(user?.has_clinician_profile || user?.user_type === 'employee' || user?.user_type === 'owner' || user?.user_type === 'superuser')}
        label="This area is for licensed clinicians."
      >{children}</_RoleGate>
    </RequireAuth>
  );
}

export function RequirePracticeAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <RequireAuth>
      <_RoleGate
        ok={Boolean(user?.can_administer_practice || user?.user_type === 'owner' || user?.user_type === 'superuser')}
        label="This area is for practice owners and managers."
      >{children}</_RoleGate>
    </RequireAuth>
  );
}

export function RequirePerm({
  perm, children, anyOf,
}: { perm?: string; anyOf?: string[]; children: ReactNode }) {
  const { hasPerm, user } = useAuth();
  const isSuperuser = user?.user_type === 'superuser';
  const ok =
    isSuperuser ||
    (perm ? hasPerm(perm) : false) ||
    (anyOf ? anyOf.some(hasPerm) : false);
  return (
    <RequireAuth>
      <_RoleGate ok={ok} label="You don't have the required permission.">
        {children}
      </_RoleGate>
    </RequireAuth>
  );
}

export function Anonymous({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <>{children}</>;
}